import React, { useEffect, useRef, useState } from 'react';

/*
  App-wide ambient background music with a clearly-visible control pill:
  an on/off toggle plus a volume slider.

  Rendered once at the app root (outside <Routes>) so the audio keeps playing
  across page navigations instead of restarting. Browsers block autoplay-with-
  sound until the user interacts, so playback begins on the first click/tap/
  keypress (when enabled). The on/off choice and volume are remembered.

  To use it: drop a royalty-free track at  public/background-music.mp3
  If the file is missing the control simply does nothing — nothing else breaks.
*/
function BackgroundMusic() {
  const audioRef = useRef(null);

  /* Default OFF — only on if the user has turned it on before */
  const [enabled, setEnabled] = useState(
    () => localStorage.getItem('bgMusicOn') === 'true'
  );
  const [volume, setVolume] = useState(() => {
    const saved = parseFloat(localStorage.getItem('bgMusicVol'));
    return Number.isFinite(saved) ? saved : 0.25;
  });
  const [playing, setPlaying] = useState(false);

  /* Music hint — shown every time a user logs in (new or returning) */
  const [showHint, setShowHint] = useState(false);

  const dismissHint = () => setShowHint(false);

  /* LoginPage fires this on a successful login */
  useEffect(() => {
    const onShow = () => setShowHint(true);
    window.addEventListener('show-music-hint', onShow);
    return () => window.removeEventListener('show-music-hint', onShow);
  }, []);

  /* Auto-dismiss the hint after a few seconds */
  useEffect(() => {
    if (!showHint) return;
    const t = setTimeout(() => setShowHint(false), 9000);
    return () => clearTimeout(t);
  }, [showHint]);

  /* Keep the audio element's volume in sync */
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    localStorage.setItem('bgMusicVol', String(volume));
  }, [volume]);

  /* Start playback on the first user gesture (autoplay policy) */
  useEffect(() => {
    if (!enabled) return;

    const tryPlay = () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.volume = volume;
      audio
        .play()
        .then(() => setPlaying(true))
        .catch(() => {});
    };

    tryPlay();

    const onGesture = () => {
      tryPlay();
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };

    window.addEventListener('pointerdown', onGesture);
    window.addEventListener('keydown', onGesture);

    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const toggle = () => {
    const audio = audioRef.current;
    const next = !enabled;

    setEnabled(next);
    localStorage.setItem('bgMusicOn', String(next));
    dismissHint();

    if (!audio) return;

    if (next) {
      audio.volume = volume;
      audio
        .play()
        .then(() => setPlaying(true))
        .catch(() => {});
    } else {
      audio.pause();
      setPlaying(false);
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={process.env.PUBLIC_URL + '/background-music.mp3'}
        loop
        preload="auto"
      />

      <div className="bg-music">
        {showHint && (
          <div className="bg-music-hint">
            <span>
              🎵 Background music is {enabled ? 'on' : 'off'} — tap here to{' '}
              {enabled ? 'turn it off' : 'turn it on'} or set volume
            </span>
            <button
              type="button"
              className="bg-music-hint-x"
              onClick={dismissHint}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        <button
          type="button"
          className={'bg-music-btn' + (enabled && playing ? ' playing' : '')}
          onClick={toggle}
          aria-label={enabled ? 'Turn music off' : 'Turn music on'}
          title={enabled ? 'Music: ON (click to turn off)' : 'Music: OFF (click to turn on)'}
        >
          <span className="bg-music-ico">{enabled ? '🎵' : '🔇'}</span>
          {enabled && playing && (
            <span className="bg-music-waves" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          )}
        </button>

        <div className="bg-music-extra">
          <span className="bg-music-label">{enabled ? 'On' : 'Off'}</span>

          <input
            type="range"
            className="bg-music-vol"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            disabled={!enabled}
            onChange={(e) => setVolume(Number(e.target.value))}
            aria-label="Music volume"
            title="Volume"
          />
        </div>
      </div>

      <style>{musicStyles}</style>
    </>
  );
}

const musicStyles = `
  /* Compact: just a small round button. Expands on hover to reveal the
     label + volume slider, so it barely takes any space. */
  .bg-music {
    position: fixed;
    left: 14px;
    bottom: 18px;
    z-index: 1051;
    display: flex;
    align-items: center;
    padding: 3px;
    border-radius: 40px;
    background: linear-gradient(135deg, #7c2d12, #ea580c);
    border: 1.5px solid rgba(253,230,138,0.65);
    box-shadow: 0 6px 16px rgba(234,88,12,0.35);
    transition: padding 0.25s ease;
  }
  .bg-music:hover { padding-right: 12px; }

  .bg-music-btn {
    position: relative;
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    font-size: 16px;
    color: #fff;
    background: rgba(0,0,0,0.18);
    cursor: pointer;
    transition: transform 0.2s ease, background 0.2s ease;
  }
  .bg-music-btn:hover { background: rgba(0,0,0,0.28); }
  .bg-music-btn.playing { animation: bgMusicPulse 2.6s ease-in-out infinite; }
  @keyframes bgMusicPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(251,191,36,0.6); }
    70% { box-shadow: 0 0 0 7px rgba(251,191,36,0); }
  }

  /* Collapsed by default; slides open on hover */
  .bg-music-extra {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 0;
    opacity: 0;
    overflow: hidden;
    transition: max-width 0.3s ease, opacity 0.2s ease, margin-left 0.3s ease;
  }
  .bg-music:hover .bg-music-extra {
    max-width: 140px;
    opacity: 1;
    margin-left: 8px;
  }

  .bg-music-label {
    color: #fff;
    font-size: 0.74rem;
    font-weight: 700;
    white-space: nowrap;
  }

  /* Equalizer bars under the icon while playing */
  .bg-music-waves {
    position: absolute;
    bottom: 2px;
    display: flex;
    align-items: flex-end;
    gap: 1.5px;
    height: 7px;
  }
  .bg-music-waves span {
    width: 2px;
    background: #fbbf24;
    border-radius: 2px;
    animation: bgMusicBar 0.9s ease-in-out infinite;
  }
  .bg-music-waves span:nth-child(1) { height: 3px; animation-delay: 0s; }
  .bg-music-waves span:nth-child(2) { height: 7px; animation-delay: 0.2s; }
  .bg-music-waves span:nth-child(3) { height: 5px; animation-delay: 0.4s; }
  @keyframes bgMusicBar {
    0%, 100% { transform: scaleY(0.4); }
    50% { transform: scaleY(1); }
  }

  .bg-music-vol {
    width: 72px;
    accent-color: #fbbf24;
    cursor: pointer;
  }
  .bg-music-vol:disabled { opacity: 0.4; cursor: not-allowed; }

  /* One-time hint bubble above the button */
  .bg-music-hint {
    position: absolute;
    bottom: calc(100% + 12px);
    left: 0;
    width: 220px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: #fff;
    color: #1e293b;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.4;
    padding: 10px 12px;
    border-radius: 14px;
    border: 1px solid #fde68a;
    box-shadow: 0 12px 30px rgba(124,45,18,0.22);
    animation: bgHintIn 0.35s ease;
  }
  /* little pointer to the button */
  .bg-music-hint::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 18px;
    border: 7px solid transparent;
    border-top-color: #fff;
  }
  @keyframes bgHintIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .bg-music-hint-x {
    flex-shrink: 0;
    border: none;
    background: #f1f5f9;
    color: #64748b;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    font-size: 0.7rem;
    line-height: 1;
    cursor: pointer;
  }
  .bg-music-hint-x:hover { background: #e2e8f0; }

  /* Mobile: stay a tiny button clear of the bottom nav. Tap toggles; the
     slider opens on tap-and-hold (:active) since there's no hover. */
  @media (max-width: 1199.98px) {
    .bg-music {
      bottom: calc(78px + env(safe-area-inset-bottom, 0px));
      left: 12px;
    }
    .bg-music:active { padding-right: 12px; }
    .bg-music:active .bg-music-extra {
      max-width: 120px;
      opacity: 1;
      margin-left: 8px;
    }
    .bg-music-vol { width: 60px; }
  }
`;

export default BackgroundMusic;
